import { buildICReportRows, IC_COVERAGE_IDS, ICReportPolicyInput, MonthlyReportHelper } from "@deathstar/types/blazar";
import {
    MonthlyReport,
    MonthlyReportPolicyData,
    MonthlyReportQueryResult,
    MonthlyReportStatus,
    Policy,
} from "@deathstar/types/northstar";
import { downloadICReport, useSnackbar } from "@deathstar/ui";
import moment from "moment";
import { QueryKey, useMutation, UseMutationOptions, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { queryClient } from "../../util/queryClient";
import { blazar } from "../util/blazar";
import { BadRequestError, NotFoundError, ResponseError, UnauthenticatedError, UnauthorizedError } from "../util/exceptions";

export const monthlyReports = {
    queryKeys: {
        find: (accountId: string) => [accountId, "monthlyReports"] as QueryKey,
        findForMonth: (accountId: string, policyId: string, year: number, month: number) =>
            [accountId, "monthlyReports", policyId, year, month] as QueryKey,
    },

    async find(accountId: string): Promise<MonthlyReportPolicyData[]> {
        const data = await blazar.fetchJson<MonthlyReportPolicyData[]>(`/waypoint/orgs/${accountId}/monthly-reports`);
        return blazar.changeDateStringsToDateObjects(data);
    },

    useFind(accountId: string, config?: Partial<UseQueryOptions<MonthlyReportPolicyData[], ResponseError>>) {
        return useQuery<MonthlyReportPolicyData[], ResponseError>({
            queryKey: monthlyReports.queryKeys.find(accountId),
            queryFn: () => monthlyReports.find(accountId),
            ...config,
        });
    },

    async findForMonth(accountId: string, policyId: string, year: number, month: number): Promise<MonthlyReportQueryResult> {
        const data = await blazar.fetchJson<MonthlyReportQueryResult>(
            `/waypoint/orgs/${accountId}/policies/${policyId}/monthly-reports/${year}/${month}`
        );
        return blazar.changeDateStringsToDateObjects(data);
    },

    useFindForMonth(
        accountId: string,
        policyId: string,
        year: number,
        month: number,
        config?: Partial<UseQueryOptions<MonthlyReportQueryResult, ResponseError>>
    ) {
        return useQuery<MonthlyReportQueryResult, ResponseError>({
            queryKey: monthlyReports.queryKeys.findForMonth(accountId, policyId, year, month),
            queryFn: () => monthlyReports.findForMonth(accountId, policyId, year, month),
            ...config,
        });
    },

    async update(
        accountId: string,
        policyId: string,
        year: number,
        month: number,
        data: Partial<Pick<MonthlyReport, "mileage" | "revenue" | "payroll">>
    ) {
        return await blazar.fetchJson<void>(`/waypoint/orgs/${accountId}/policies/${policyId}/monthly-reports/${year}/${month}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        });
    },

    /** Does not automatically invalidate the query and refresh the data */
    useUpdate(
        accountId: string,
        policyId: string,
        year: number,
        month: number,
        config?: UseMutationOptions<void, ResponseError, Partial<Pick<MonthlyReport, "mileage" | "revenue" | "payroll">>>
    ) {
        return useMutation<void, ResponseError, Partial<Pick<MonthlyReport, "mileage" | "revenue" | "payroll">>>({
            mutationFn: (data) => monthlyReports.update(accountId, policyId, year, month, data),
            ...config,
        });
    },

    async create(accountId: string, policyId: string, year: number, month: number) {
        return await blazar.fetchJson<void>(`/waypoint/orgs/${accountId}/policies/${policyId}/monthly-reports/${year}/${month}`, {
            method: "POST",
        });
    },

    useCreate: (accountId: string, policyId: string, config?: UseMutationOptions<void, ResponseError, { year: number; month: number }>) => {
        return useMutation<void, ResponseError, { year: number; month: number }>({
            mutationFn: ({ year, month }) => monthlyReports.create(accountId, policyId, year, month),
            ...config,
            onSuccess: (...args) => {
                config?.onSuccess?.(...args);
                queryClient.invalidateQueries({ queryKey: monthlyReports.queryKeys.find(accountId) });
            },
        });
    },

    async submit(accountId: string, policyId: string, year: number, month: number) {
        return blazar
            .fetchJson<void>(`/waypoint/orgs/${accountId}/policies/${policyId}/monthly-reports/${year}/${month}/submit`, {
                method: "POST",
            })
            .then(() => {
                try {
                    window.trace.trackEvent("monthly_report_submitted");
                } catch (e) {
                    console.error(e);
                }
            });
    },

    useSubmit: (accountId: string, config?: UseMutationOptions<void, ResponseError, { policyId: string; year: number; month: number }>) => {
        return useMutation<void, ResponseError, { policyId: string; year: number; month: number }>({
            mutationFn: ({ policyId, year, month }) => monthlyReports.submit(accountId, policyId, year, month),
            ...config,
            onSuccess: (data, vars, ...args) => {
                config?.onSuccess?.(data, vars, ...args);
                queryClient.invalidateQueries({ queryKey: monthlyReports.queryKeys.find(accountId) });
            },
        });
    },

    async generateIndependentContractorReport(accountId: string) {
        try {
            const response = await blazar.fetch(`/waypoint/orgs/${accountId}/independent-contractor-report`, {
                method: "POST",
            });
            const blob = await response.blob();
            const a = document.createElement("a");
            a.style.display = "none";
            document.body.appendChild(a);
            a.href = URL.createObjectURL(blob);
            a.download = "Independent Contractor Report.xlsx";
            a.click();
            document.body.removeChild(a);
        } catch (e) {
            if (e instanceof NotFoundError) {
                useSnackbar.add("No submitted/finalized report found for last month", { variant: "error" });
                return;
            }
            if (e instanceof UnauthenticatedError) {
                useSnackbar.add("Your session has expired. Please login to continue", { variant: "error" });
                return;
            }
            if (e instanceof UnauthorizedError) {
                useSnackbar.add("You do not have access to this feature", { variant: "error" });
                return;
            }
            if (e instanceof BadRequestError) {
                useSnackbar.add(e.message, { variant: "error" });
                return;
            }

            useSnackbar.add("Could not open report. Please try again later.", { variant: "error" });
        }
    },


    /**
     * Builds the independent-contractor report in the browser instead of on the server, using the
     * shared roll-up that the Northstar reporting page uses — so both produce the same numbers.
     *
     * Gating deliberately matches the server endpoint it replaces: the report covers the last
     * completed month, and every policy that reports on that month must have been submitted or
     * finalized first.
     */
    async generateIndependentContractorReportClientSide(accountId: string, clientName: string) {
        const policies = await monthlyReports.find(accountId);

        const reportDate = moment.utc().startOf("month").subtract(1, "day");
        const year = reportDate.year();
        const month = reportDate.month() + 1;

        const isThisPeriod = (m: { year: number; month: number }) => m.year === year && m.month === month;

        const eligible = policies.filter(
            (policy) =>
                [Policy.Status.ACTIVE, Policy.Status.PRE_CANCELLATION].includes(policy.policyStatus) &&
                policy.availableMonths.some((m) => isThisPeriod(m) && m.status !== MonthlyReportStatus.NotRequired)
        );

        if (!eligible.length) {
            throw new Error("No submitted/finalized report found for last month");
        }

        const isComplete = (status: MonthlyReportStatus) =>
            status === MonthlyReportStatus.Submitted ||
            status === MonthlyReportStatus.Finalized ||
            status === MonthlyReportStatus.NotRequired;

        const incomplete = eligible.some((policy) =>
            policy.availableMonths.some((m) => isThisPeriod(m) && !isComplete(m.status))
        );

        if (incomplete) {
            throw new Error(`All reports for ${month}/${year} must be completed before downloading the IC report.`);
        }

        const results = await Promise.all(
            eligible.map((policy) => monthlyReports.findForMonth(accountId, policy.policyId, year, month))
        );

        const inputs: ICReportPolicyInput[] = [];
        results.forEach((result, index) => {
            // getMonthlyReportData throws when no layer covers the month, which is reachable when
            // policies on one account have different terms
            if (!result?.layers?.length) return;
            try {
                inputs.push({
                    policyNumber: eligible[index].policyNumber,
                    data: MonthlyReportHelper.getMonthlyReportData(result.report, result.layers, result.equipment),
                    equipment: result.equipment,
                });
            } catch (e) {
                console.error(`Could not calculate the ${year}/${month} report for policy ${eligible[index].policyNumber}`, e);
            }
        });

        if (!inputs.length) {
            throw new Error("No reports could be calculated for last month");
        }

        const rows = buildICReportRows(inputs);
        const coverageOptionIds = IC_COVERAGE_IDS.filter((id) => rows.some((row) => (row.costs.get(id) ?? 0) !== 0));

        downloadICReport(
            {
                clientName,
                policyNumbers: eligible.map((policy) => policy.policyNumber),
                period: { year, month },
                coverageOptionIds,
            },
            rows
        );
    },

    useGenerateIndependentContractorReportClientSide: (accountId: string, clientName: string) => {
        return useMutation<void, Error>({
            mutationFn: () => monthlyReports.generateIndependentContractorReportClientSide(accountId, clientName),
        });
    },

    useGenerateIndependentContractorReport: (accountId: string) => {
        return useMutation<void, ResponseError>({
            mutationFn: () => monthlyReports.generateIndependentContractorReport(accountId),
        });
    },
};
